/**
 * @file index.tsx
 *
 * @fileoverview Configuration for the job wizard. What steps there are and which page is which step.
 */
import { FormattedMessage } from 'react-intl';
import { WizardStep } from '~/jobiqo/models/wizard';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';

export enum StepKeys {
  upgrades = 'upgrades',
  billing = 'billing',
  bundles = 'bundles',
  type = 'type',
  form = 'form',
  destination = 'destination',
  destinationUpgrades = 'destinationUpgrades',
}

export enum JobWizardFlow {
  New = 'new',
  Republish = 'edit',
  Upgrades = 'addons',
}

interface JobWizardStep extends WizardStep {
  /**
   * The key of the step.
   */
  key: StepKeys;
}

const getCommonSteps = (): JobWizardStep[] => {
  const { JOBS } = getCurrentFrontendConfig();
  const showDestination = JOBS.JOB_POSTING_WIZARD_SHOW_DESTINATION;
  const separateUpgradesStep =
    JOBS.DESTINATION_PAGE_CONFIG?.SEPARATE_UPGRADES_STEP;

  return [
    {
      key: StepKeys.upgrades,
      title: (
        <FormattedMessage
          id="job_wizard_step_upgrades"
          defaultMessage="Upgrades"
        />
      ),
      page: '/job/wizard/upgrades/[...params]',
    },
    ...(showDestination
      ? [
          {
            key: StepKeys.destination,
            title: (
              <FormattedMessage
                id="job_wizard_step_destination"
                defaultMessage="Destination"
              />
            ),
            page: '/job/wizard/destination/[...params]',
          },
        ]
      : []),
    ...(showDestination && separateUpgradesStep
      ? [
          {
            key: StepKeys.destinationUpgrades,
            title: (
              <FormattedMessage
                id="job_wizard_step_destination_upgrades"
                defaultMessage="Upgrades"
              />
            ),
            page: '/job/wizard/destination-upgrades/[...params]',
          },
        ]
      : []),
    {
      key: StepKeys.billing,
      title: (
        <FormattedMessage
          id="job_wizard_step_checkout"
          defaultMessage="Checkout"
        />
      ),
      page: '/job/wizard/billing/[...params]',
    },
  ];
};

/**
 *
 * Handles special cases for when steps are optional.
 *
 * @param steps
 *   The current flow steps.
 * @param showBundlesStep
 *   If the bundles step should be shown.
 * @param showUpgradesStep
 *   Boolean if upgrades step should be shown.
 * @param showDestinationStep
 *   Boolean if destination step should be shown.
 * @param showDestinationUpgradesStep
 *  Boolean if a separate destination upgrades step should be shown.
 *
 * @returns
 *   The list of final steps.
 */
const calculateStepsWithInfo = (
  steps,
  showBundlesStep: boolean,
  showUpgradesStep: boolean,
  showDestinationStep: boolean,
  showDestinationUpgradesStep: boolean,
) => {
  return steps.filter((step) => {
    return (
      (step.key !== StepKeys.bundles || showBundlesStep) &&
      (step.key !== StepKeys.upgrades || showUpgradesStep) &&
      (step.key !== StepKeys.destination || showDestinationStep) &&
      (step.key !== StepKeys.destinationUpgrades || showDestinationUpgradesStep)
    );
  });
};

export const getStepsinfo = (
  republishFlow,
  showBundlesStep: boolean,
  showUpgradesStep: boolean = true,
  showDestinationStep: boolean = false,
  showDestinationUpgradesStep: boolean = false,
) => {
  const { JOBS } = getCurrentFrontendConfig();
  const separateUpgradesStep =
    JOBS.DESTINATION_PAGE_CONFIG?.SEPARATE_UPGRADES_STEP;
  const commonSteps = getCommonSteps();
  const stepsInfo = [
    {
      key: StepKeys.bundles,
      title: (
        <FormattedMessage
          id="job_wizard_step_bundles"
          defaultMessage="Products"
        />
      ),
      page: '/job/wizard/bundles/[[...params]]',
    },
    {
      key: StepKeys.type,
      title: (
        <FormattedMessage
          id="job_wizard_step_choose_job"
          defaultMessage="Type"
        />
      ),
      page: '/job/wizard/type',
    },
    {
      key: StepKeys.form,
      title: <FormattedMessage id="job_wizard_step_job" defaultMessage="Job" />,
      page: '/job/wizard/form',
    },
    ...commonSteps,
  ];
  const republishStepsInfo = [
    {
      key: StepKeys.bundles,
      title: (
        <FormattedMessage
          id="job_wizard_step_bundles"
          defaultMessage="Products"
        />
      ),
      page: '/job/wizard/bundles/[[...params]]',
    },
    {
      key: StepKeys.form,
      title: <FormattedMessage id="job_wizard_step_job" defaultMessage="Job" />,
      page: '/job/wizard/form/[jobId]',
    },
    ...commonSteps,
  ];
  const upgradesStepInfo = commonSteps;

  switch (republishFlow) {
    case JobWizardFlow.New:
      return calculateStepsWithInfo(
        stepsInfo,
        showBundlesStep,
        showUpgradesStep,
        showDestinationStep,
        showDestinationUpgradesStep,
      );

    case JobWizardFlow.Republish:
      return calculateStepsWithInfo(
        republishStepsInfo,
        showBundlesStep,
        showUpgradesStep,
        showDestinationStep,
        showDestinationUpgradesStep,
      );

    case JobWizardFlow.Upgrades:
      return calculateStepsWithInfo(
        upgradesStepInfo,
        showBundlesStep,
        showUpgradesStep,
        showDestinationStep,
        separateUpgradesStep ? false : showDestinationUpgradesStep,
      );

    default:
      return stepsInfo;
  }
};
